import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setAreasIds,
  setDevelopersIds,
  setOfferFilters
} from '../../../../redux/slices/offersSlice';
import { OffersFilter } from '../../dtos';
import MFiltersDrawer from './mobile/MFiltersDrawer';
import NewArrowIcon from '../../../../new-components/svg-icons/NewArrowIcon';
import { useTranslation } from 'next-i18next';
import { logEvent } from '../../../../new-lib/analytics';
import styled from 'styled-components';
import SelectionDropdown from './SelectionDropdown';

interface SelectionFilterProps {
  title: string;
  type: 'offer_type' | 'Developers_Search' | 'Areas_search';
  selections: OffersFilter[];
  filterDropdown: 'offer_type' | 'Developers_Search' | 'Areas_search' | null;
  setFilterDropdown: Dispatch<
    SetStateAction<'offer_type' | 'Developers_Search' | 'Areas_search' | null>
  >;
}

const SelectionFilter = ({
  title,
  type,
  selections,
  filterDropdown,
  setFilterDropdown
}: SelectionFilterProps) => {
  const filterRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation('cityscape');
  const isMobile = useSelector((state: any) => state.app.isMobile);
  const developerIds = useSelector((state: any) => state.offers.developers_ids);
  const areasIds = useSelector((state: any) => state.offers.areas_ids);
  const offersFilters = useSelector(
    (state: any) => state.offers.offers_filters
  );
  const offersExclusive = useSelector(
    (state: any) => state.offers.isNawyExclusive
  );

  useEffect(() => {
    if (isMobile) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, [developerIds, areasIds, offersFilters, offersExclusive]);

  const dispatch = useDispatch();

  const handleClick = () => {
    if (type === filterDropdown) {
      setFilterDropdown(null);
    } else {
      setFilterDropdown(type);
    }
  };

  const getSelectionsCount = useCallback(() => {
    if (type === 'offer_type') {
      return offersFilters.length;
    }

    if (type === 'Developers_Search') {
      return developerIds.length;
    }

    if (type === 'Areas_search') {
      return areasIds.length;
    }

    return null;
  }, [type, offersFilters, developerIds, areasIds]);

  const handleSelectionClick = (selection: OffersFilter) => {
    if (type === 'offer_type') {
      dispatch(setOfferFilters(selection.key));
    }

    if (type === 'Developers_Search') {
      dispatch(setDevelopersIds(selection.id));
    }

    if (type === 'Areas_search') {
      dispatch(setAreasIds(selection.id));
    }
  };

  const handleResetFilterClick = () => {
    if (type === 'offer_type') {
      dispatch(setOfferFilters(''));
    }

    if (type === 'Developers_Search') {
      dispatch(setDevelopersIds([]));
    }

    if (type === 'Areas_search') {
      dispatch(setAreasIds([]));
    }

    logEvent.click('reset_filter', 'offer_filters', 'offers', {
      offers_filters: {
        type: type.toLowerCase() as
          | 'nawy_exclusive'
          | 'areas_search'
          | 'developers_search'
          | 'offer_types'
      }
    });
  };

  const renderSelection = useCallback(() => {
    if (filterDropdown !== type) return null;

    if (isMobile) {
      return (
        <MFiltersDrawer
          isOpen={true}
          snapPoints={[1, 0]}
          handleCloseDrawer={handleClick}
          headerTitle={`filter_by_${filterDropdown
            .split('_')[0]
            .toLowerCase()}`}
          handleResetFilter={handleResetFilterClick}
        >
          <SelectionDropdown
            type={type}
            selections={selections}
            handleClick={handleSelectionClick}
            setFilterDropdown={setFilterDropdown}
          />
        </MFiltersDrawer>
      );
    }

    return (
      <SelectionDropdown
        type={type}
        selections={selections}
        handleClick={handleSelectionClick}
        setFilterDropdown={setFilterDropdown}
      />
    );
  }, [isMobile, filterDropdown, selections]);

  return (
    <Container dropdownOpen={filterDropdown === type} ref={filterRef}>
      <div className="filter-chip" onClick={handleClick}>
        <span>{t(title)}</span>
        <span className="count-arrow-section">
          {getSelectionsCount() > 0 && (
            <span className="filters-count">{getSelectionsCount()}</span>
          )}
          <NewArrowIcon />
        </span>
      </div>
      {renderSelection()}
    </Container>
  );
};

const Container = styled.div<{ dropdownOpen: boolean }>`
  position: relative;
  display: flex;
  user-select: none;

  .filter-chip {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-width: 150px;
    height: 40px;
    padding: 8px;
    border-radius: 12px;
    background: #f4f9fe;
    cursor: pointer;

    .count-arrow-section {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    span {
      color: #1a2028;
      text-align: center;
      font-size: 14px;
      font-family: materialMedium;
      user-select: none;
      white-space: nowrap;

      .filters-count {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        font-size: 10px;
        border-radius: 50%;
        font-family: materialMedium;
        background-color: orange;
        color: white;
      }
    }

    @media screen and (max-width: ${props =>
        props.theme.breakPoints.mobile.max}) {
      min-width: fit-content;
    }
  }
`;

export default SelectionFilter;
